/**
 * Custom Element: nosto-dynamic-card
 * Charge dynamiquement les templates Liquid pour les produits Nosto
 * Utilise /collections/{handle}/products/{handle} sur les pages collection
 * pour avoir accès aux metafields de la collection
 *
 * Optimisations :
 * - IntersectionObserver : les cartes below-the-fold ne fetchent que quand elles approchent du viewport
 * - FetchQueue : limite les fetch concurrentes pour ne pas saturer le réseau
 */

// Cache global pour éviter les requêtes dupliquées
const cardCache = new Map();

// --- FetchQueue : limite la concurrence des fetch ---
const fetchQueue = {
	maxConcurrent: 4,
	running: 0,
	queue: [],

	enqueue(fn, priority = false) {
		return new Promise((resolve, reject) => {
			const task = { fn, resolve, reject };
			if (priority) {
				this.queue.unshift(task);
			} else {
				this.queue.push(task);
			}
			this.flush();
		});
	},

	flush() {
		while (this.running < this.maxConcurrent && this.queue.length > 0) {
			const { fn, resolve, reject } = this.queue.shift();
			this.running++;
			fn()
				.then(resolve)
				.catch(reject)
				.finally(() => {
					this.running--;
					this.flush();
				});
		}
	}
};

// --- IntersectionObserver partagé (singleton) ---
let sharedObserver = null;

function getSharedObserver() {
	if (!sharedObserver) {
		sharedObserver = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						const card = entry.target;
						sharedObserver.unobserve(card);
						card.loadProductCard();
					}
				}
			},
			{ rootMargin: '0px 0px 200px 0px' }
		);
	}
	return sharedObserver;
}

class NostoDynamicCard extends HTMLElement {
	constructor() {
		super();
		this.handle = this.getAttribute('handle');
		this.template = this.getAttribute('template') || 'card';
		this.loaded = false;
	}

	connectedCallback() {
		if (!window.nostoEnabled) return;
		if (this.loaded) return;

		const isEager = this.getAttribute('data-priority') === 'eager';

		if (isEager || cardCache.has(this.getCacheKey())) {
			this.loadProductCard();
		} else {
			getSharedObserver().observe(this);
		}
	}

	disconnectedCallback() {
		if (sharedObserver) {
			sharedObserver.unobserve(this);
		}
	}

	/**
	 * Détecte le handle de la collection depuis l'URL ou un data-attribute
	 */
	getCollectionHandle() {
		// 1. Vérifier si un data-attribute force le contexte collection
		const forcedCollection = this.closest('[data-collection-handle]')?.dataset.collectionHandle;
		if (forcedCollection) return forcedCollection;

		// 2. Détecter depuis l'URL
		const match = window.location.pathname.match(/\/collections\/([^\/\?]+)/);
		return match ? match[1] : null;
	}

	/**
	 * Construit l'URL de fetch selon le contexte
	 */
	buildFetchUrl() {
		const root = window.Shopify?.routes?.root || '/';
		const collectionHandle = this.getCollectionHandle();

		if (collectionHandle) {
			// Sur une collection : utiliser l'URL collection/product pour avoir accès à collection
			return `${root}collections/${collectionHandle}/products/${this.handle}?view=${this.template}`;
		}

		// Hors collection : URL produit simple
		return `${root}products/${this.handle}?view=${this.template}`;
	}

	/**
	 * Génère une clé de cache unique (inclut le contexte collection)
	 */
	getCacheKey() {
		const collectionHandle = this.getCollectionHandle();
		return collectionHandle
			? `${collectionHandle}:${this.handle}:${this.template}`
			: `${this.handle}:${this.template}`;
	}

	async loadProductCard() {
		if (!this.handle) {
			console.warn('[nosto-dynamic-card] No handle provided');
			return;
		}

		const cacheKey = this.getCacheKey();

		// Vérifier le cache
		if (cardCache.has(cacheKey)) {
			this.innerHTML = cardCache.get(cacheKey);
			this.loaded = true;
			this.dispatchLoadedEvent();
			return;
		}

		const isFirst = this.template === 'card-first';
		const isEager = this.getAttribute('data-priority') === 'eager';

		try {
			const url = this.buildFetchUrl();
			let html;

			if (isFirst) {
				// Première carte : fetch direct, sans queue, garantit l'affichage en premier
				const response = await fetch(url);
				if (!response.ok) throw new Error(`Failed to load product card: ${response.status}`);
				html = await response.text();
			} else {
				// Autres cartes : passent par la queue (max 4 en parallèle)
				html = await fetchQueue.enqueue(
					() => fetch(url).then((r) => {
						if (!r.ok) throw new Error(`Failed to load product card: ${r.status}`);
						return r.text();
					}),
					isEager
				);
			}

			// Mettre en cache
			cardCache.set(cacheKey, html);

			this.innerHTML = html;
			this.loaded = true;
			this.dispatchLoadedEvent();

		} catch (error) {
			console.error('[nosto-dynamic-card] Error loading card:', this.handle, error);
			this.innerHTML = `<div class="product-card product-card--error">
				<p>Erreur de chargement</p>
			</div>`;
		}
	}

	dispatchLoadedEvent() {
		this.dispatchEvent(new CustomEvent('nosto:card:loaded', {
			bubbles: true,
			detail: {
				handle: this.handle,
				template: this.template
			}
		}));

		// Re-trigger Skeepers widget to populate stars on dynamically loaded cards
		if (window.SkeepersProductWidget?.generate) {
			window.SkeepersProductWidget.generate();
		}
	}

	// Observer les changements d'attributs pour recharger si nécessaire
	static get observedAttributes() {
		return ['handle', 'template'];
	}

	attributeChangedCallback(_name, oldValue, newValue) {
		if (oldValue !== newValue && this.loaded) {
			this.loaded = false;
			this.loadProductCard();
		}
	}
}

// Enregistrer le custom element
if (!customElements.get('nosto-dynamic-card')) {
	customElements.define('nosto-dynamic-card', NostoDynamicCard);
}

// Exposer une fonction pour vider le cache si nécessaire
window.NostoDynamicCard = {
	clearCache: () => cardCache.clear()
};
